/**
 * Frontend PTC (Probability to Close) score calculator.
 * Mirrors the backend implementation so manual edits can immediately
 * recompute scores without waiting for Cloud Functions.
 *
 * Algorithm (additive, not subtractive):
 *   Step 1 — Cash offer check (first): cash → 95 (inspection waived) or 90
 *   Step 2 — Pre-approval check: missing → score = 40, done
 *   Step 3 — Lender experience points (base score)
 *   Step 4 — LTV & loan type points (additive)
 *   Step 5 — Contingency deductions
 */

export interface PTCCalculationInput {
  isCashOffer?: boolean;
  inspectionWaived?: boolean;       // for cash offer path
  hasPreApproval?: boolean;
  lenderExperienceYears?: number;   // undefined/null → default to <1 yr = 25 pts
  loanAmount?: number;
  purchasePrice?: number;
  downPayment?: number;
  loanType?: string;                // 'Conventional' | 'FHA' | 'VA' | 'USDA' | 'Jumbo'
  contingencies?: {
    appraisalContingency?: boolean;
    homeInspectionContingency?: boolean;
    saleOfBuyerPropertyContingency?: boolean;
  };
}

export interface PTCBreakdown {
  // Path taken
  isCashOffer: boolean;
  hasPreApproval: boolean;
  // Lender experience
  lenderExperienceYears: number;
  lenderExperiencePoints: number;
  // LTV
  ltvPercent: number | null;
  ltvPoints: number;
  loanType: string;
  // Contingency deductions
  appraisalContingencyDeduction: number;
  homeInspectionContingencyDeduction: number;
  saleOfBuyerPropertyContingencyDeduction: number;
  totalContingencyDeduction: number;
}

export interface PTCScoreResult {
  ptcScore: number;
  ptcBreakdown: PTCBreakdown;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Step 3: Lender experience → base points */
function getLenderExperiencePoints(years?: number | null): number {
  if (years === undefined || years === null) return 25; // default: < 1 year
  if (years <= 1) return 25;    // < 1 treated as ≤ 1 per spec (exactly 1.0 → 25)
  if (years <= 3) return 35;    // > 1 and ≤ 3
  if (years <= 5) return 45;    // > 3 and ≤ 5
  if (years < 10) return 50;    // > 5 and < 10
  return 55;                    // ≥ 10
}

/** Step 4: LTV points lookup table */
function getLtvPoints(ltv: number, loanType: string): number {
  const t = (loanType || 'CONVENTIONAL').toUpperCase();

  if (ltv === 100) {
    if (t === 'USDA') return 35;
    if (t === 'VA')   return 32.5;
    // FHA, CONVENTIONAL, JUMBO → 0
    return 0;
  }
  if (ltv > 95) {
    // > 95 and < 100
    if (t === 'USDA' || t === 'CONVENTIONAL') return 37.5;
    if (t === 'FHA'  || t === 'VA')           return 35;
    if (t === 'JUMBO')                         return 0;
    return 37.5;
  }
  if (ltv > 90) {
    // > 90 and ≤ 95
    if (t === 'USDA')         return 40;
    if (t === 'FHA')          return 36;
    if (t === 'VA')           return 37.5;
    if (t === 'CONVENTIONAL') return 37.5;
    if (t === 'JUMBO')        return 30;
    return 37.5;
  }
  if (ltv > 85) {
    // > 85 and ≤ 90
    if (t === 'USDA')         return 40;
    if (t === 'FHA')          return 37.5;
    if (t === 'VA')           return 37.5;
    if (t === 'CONVENTIONAL') return 40;
    if (t === 'JUMBO')        return 32.5;
    return 40;
  }
  if (ltv > 80) {
    // > 80 and ≤ 85
    if (t === 'USDA')         return 40;
    if (t === 'FHA')          return 37.5;
    if (t === 'VA')           return 40;
    if (t === 'CONVENTIONAL') return 41.5;
    if (t === 'JUMBO')        return 32.5;
    return 41.5;
  }
  if (ltv > 75) {
    // > 75 and ≤ 80
    if (t === 'USDA')         return 40;
    if (t === 'FHA')          return 40;
    if (t === 'VA')           return 40;
    if (t === 'CONVENTIONAL') return 42.5;
    if (t === 'JUMBO')        return 40;
    return 42.5;
  }
  // ≤ 75
  if (t === 'USDA')         return 40;
  if (t === 'FHA')          return 42.5;
  if (t === 'VA')           return 42.5;
  if (t === 'CONVENTIONAL') return 45;
  if (t === 'JUMBO')        return 45;
  return 45;
}

/** Attempt to compute LTV; returns null when data is insufficient */
function tryComputeLtv(
  loanAmount?: number,
  purchasePrice?: number,
  downPayment?: number
): number | null {
  const loan = loanAmount ?? 0;
  if (!loan) return null;

  let price = purchasePrice;
  if (!price && downPayment !== undefined && downPayment !== null) {
    price = loan + downPayment;
  }
  if (!price || price <= 0) return null;

  return (loan / price) * 100;
}

// ---------------------------------------------------------------------------
// Main exported functions
// ---------------------------------------------------------------------------

/** Central mapper: converts raw extractedData (any shape) to PTCCalculationInput */
export function buildPtcInputFromOffer(
  extractedData: any,
  lenderExperienceYears?: number
): PTCCalculationInput {
  if (!extractedData) return {};

  const financing = extractedData.financing ?? {};
  const transaction = extractedData.transaction ?? {};
  const preApproval = extractedData.pre_approval ?? {};
  const contingencies = extractedData.contingencies ?? {};
  const meta = extractedData._meta ?? {};

  const paymentMethod: string = (financing.payment_method ?? '').toLowerCase();
  const isCashOffer = paymentMethod === 'cash';

  // For cash offers, inspectionWaived = inspection.waived === true
  const inspectionWaived =
    contingencies.inspection?.waived === true;

  // Prefer pre_approval.loan_type over financing.mortgage_type when present
  const rawLoanType: string =
    (preApproval.loan_type && String(preApproval.loan_type).trim())
      ? String(preApproval.loan_type).trim()
      : (financing.mortgage_type ?? '');

  // Prefer pre_approval.base_loan_amount (most authoritative) over financing.mortgage_amount
  const resolvedLoanAmount =
    preApproval.base_loan_amount ?? financing.mortgage_amount ?? undefined;

  return {
    isCashOffer,
    inspectionWaived,
    hasPreApproval: meta.has_pre_approval,
    lenderExperienceYears,
    loanAmount: resolvedLoanAmount,
    purchasePrice: transaction.final_sales_price ?? undefined,
    downPayment: financing.down_payment_amount ?? undefined,
    loanType: rawLoanType || undefined,
    contingencies: {
      appraisalContingency: contingencies.appraisal === true,
      homeInspectionContingency:
        contingencies.inspection?.waived === true
          ? false                              // waived = no contingency
          : contingencies.inspection != null
            ? contingencies.inspection !== false
            : undefined,
      saleOfBuyerPropertyContingency:
        contingencies.sale_of_buyers_property === true,
    },
  };
}

/** Returns true when no explicit lender experience years were provided */
export const isUsingDefaultLenderExperience = (years?: number | null): boolean =>
  years === undefined || years === null;

/** Main calculation function — implements the 5-step additive algorithm */
export function calculatePtcScore(input: PTCCalculationInput): PTCScoreResult {
  const isCashOffer = input.isCashOffer === true;
  const loanType = (input.loanType || 'CONVENTIONAL').toUpperCase();

  // -------------------------------------------------------------------------
  // Step 1 — Cash offer check (FIRST)
  // -------------------------------------------------------------------------
  if (isCashOffer) {
    const waived = input.inspectionWaived === true;
    const score = waived ? 95 : 90;

    return {
      ptcScore: score,
      ptcBreakdown: {
        isCashOffer: true,
        hasPreApproval: true,           // not applicable for cash
        lenderExperienceYears: 0,
        lenderExperiencePoints: 0,
        ltvPercent: null,
        ltvPoints: 0,
        loanType,
        appraisalContingencyDeduction: 0,
        homeInspectionContingencyDeduction: 0,
        saleOfBuyerPropertyContingencyDeduction: 0,
        totalContingencyDeduction: 0,
      },
    };
  }

  // -------------------------------------------------------------------------
  // Step 2 — Pre-approval check (mortgage offers only)
  // -------------------------------------------------------------------------
  const hasPreApproval = input.hasPreApproval !== false; // default true when undefined
  if (input.hasPreApproval === false) {
    return {
      ptcScore: 40,
      ptcBreakdown: {
        isCashOffer: false,
        hasPreApproval: false,
        lenderExperienceYears: 0,
        lenderExperiencePoints: 0,
        ltvPercent: null,
        ltvPoints: 0,
        loanType,
        appraisalContingencyDeduction: 0,
        homeInspectionContingencyDeduction: 0,
        saleOfBuyerPropertyContingencyDeduction: 0,
        totalContingencyDeduction: 0,
      },
    };
  }

  // -------------------------------------------------------------------------
  // Step 3 — Lender experience points (base score)
  // -------------------------------------------------------------------------
  const resolvedYears = input.lenderExperienceYears ?? null;
  const lenderExperiencePoints = getLenderExperiencePoints(resolvedYears);
  let score = lenderExperiencePoints;

  // -------------------------------------------------------------------------
  // Step 4 — LTV & loan type points (additive)
  // -------------------------------------------------------------------------
  const ltvPercent = tryComputeLtv(
    input.loanAmount,
    input.purchasePrice,
    input.downPayment
  );
  let ltvPoints = 0;
  if (ltvPercent !== null) {
    ltvPoints = getLtvPoints(ltvPercent, loanType);
    score += ltvPoints;
  }

  // -------------------------------------------------------------------------
  // Step 5 — Contingency deductions
  // -------------------------------------------------------------------------
  const appraisalDeduction =
    input.contingencies?.appraisalContingency === true ? 3 : 0;
  const homeInspectionDeduction =
    input.contingencies?.homeInspectionContingency === true ? 5 : 0;
  const saleOfBuyerPropertyDeduction =
    input.contingencies?.saleOfBuyerPropertyContingency === true ? 15 : 0;
  const totalContingencyDeduction =
    appraisalDeduction + homeInspectionDeduction + saleOfBuyerPropertyDeduction;

  score -= totalContingencyDeduction;

  // -------------------------------------------------------------------------
  // Final clamp and round
  // -------------------------------------------------------------------------
  const ptcScore = Math.round(Math.max(0, Math.min(100, score)) * 100) / 100;

  return {
    ptcScore,
    ptcBreakdown: {
      isCashOffer: false,
      hasPreApproval,
      lenderExperienceYears: resolvedYears ?? 0,
      lenderExperiencePoints,
      ltvPercent: ltvPercent !== null ? Math.round(ltvPercent * 100) / 100 : null,
      ltvPoints,
      loanType,
      appraisalContingencyDeduction: appraisalDeduction,
      homeInspectionContingencyDeduction: homeInspectionDeduction,
      saleOfBuyerPropertyContingencyDeduction: saleOfBuyerPropertyDeduction,
      totalContingencyDeduction,
    },
  };
}
